import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../api";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

const HCP_REVIEW_FORMATS = ['Singles Stableford', 'Singles Medal', 'Singles Stroke Play'];
const BETTER_BALL_HCP_FORMATS = ["Better Ball Stableford", "Better Ball Medal", "Better Ball Stroke Play", "Fourball Better Ball Stableford", "Fourball Stroke Play"];
// Mike's authoritative HCP allowance table (confirmed 2026-09-04) — see the
// matching constant in IndividualScoreEntry.jsx for the full explanation.
const HCP_95_FORMATS = ["Singles Stableford", "Singles Medal", "Singles Stroke Play", "Team Stableford", "Team Medal", "Team Aggregate Stableford", "Team Aggregate Stroke Play"];

// Same convention as Players.jsx -- whole-number WHS handicaps display
// plain (24, not 24.0), genuine decimals keep one decimal place (5.8).
const fmtHcp = (h) => { const n = Number(h) || 0; return n % 1 === 0 ? String(n) : n.toFixed(1); };

export default function IndividualResults() {
  const [event, setEvent] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [unlockModal, setUnlockModal] = useState(null);
  const [unlockReason, setUnlockReason] = useState("");
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [showMultiDay, setShowMultiDay] = useState(false);
  const [multiDayName, setMultiDayName] = useState("");
  const [multiDayDesc, setMultiDayDesc] = useState("");
  const [archivedEvents, setArchivedEvents] = useState([]);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [multiDayBusy, setMultiDayBusy] = useState(false);
  const [prizes, setPrizes] = useState(null);
  const [holes, setHoles] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const nearestPinRef = React.useRef("");
  const nearestPin2Ref = React.useRef("");
  const longestDriveRef = React.useRef("");
  const navigate = useNavigate();
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      setSelectedPlayer(null);
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) { setLoading(false); return; }
      setEvent(eventRes.data);
      const [scoresRes, playersRes, holesRes, prizesRes] = await Promise.all([
        api.get("/scores/" + eventRes.data.id),
        api.get("/groups"),
        api.get("/courses/" + eventRes.data.course_id + "/holes"),
        api.get("/events/prizes").catch(() => ({ data: { nearest_pin: "", nearest_pin2: "", longest_drive: "" } }))
      ]);
      // Pre-fill from whatever was already saved (e.g. via Winners & Losers) so
      // Archive Results carries it over instead of silently archiving blanks.
      nearestPinRef.current = prizesRes.data.nearest_pin || "";
      nearestPin2Ref.current = prizesRes.data.nearest_pin2 || "";
      longestDriveRef.current = prizesRes.data.longest_drive || "";
      setPrizes(prizesRes.data);
      const players = (playersRes.data.players || []).filter(p => p.playing);
      const holes = holesRes.data;
      setHoles(holes);
      const scores = scoresRes.data;
      const openMultiDay = async () => {
    try {
      const res = await api.get("/archive/list");
      setArchivedEvents(res.data || []);
      setShowMultiDay(true);
    } catch(e) { showToast("Failed to load archived events"); }
  };

  const saveMultiDay = async () => {
    if (!multiDayName.trim()) { showToast("Please enter a competition name"); return; }
    if (selectedEventIds.length < 2) { showToast("Please select at least 2 days"); return; }
    setMultiDayBusy(true);
    try {
      await api.post("/archive/multiday", { name: multiDayName, description: multiDayDesc, archived_event_ids: selectedEventIds });
      showToast("Multi-day competition saved!");
      setShowMultiDay(false);
      setMultiDayName(""); setMultiDayDesc(""); setSelectedEventIds([]);
    } catch(e) { showToast("Failed to save: " + (e.response?.data?.error || e.message)); }
    finally { setMultiDayBusy(false); }
  };

  const isMedal = eventRes.data.game_type?.includes("Medal") || eventRes.data.game_type?.includes("Stroke");
      const isBetterBall = BETTER_BALL_HCP_FORMATS.includes(eventRes.data.game_type);

      // Better Ball plays off 85% of individual handicap, per club
      // convention — never the event's manual HCP% field, which is only
      // for individual formats. Confirmed with Mike 2026-08-31.
      //
      // Singles Match Play's own rule (confirmed 2026-09-04, this format
      // only): Playing HCP = player's own Course HCP minus the LOWEST
      // Course HCP in their own physical group (grp) — not a percentage
      // like every other format here.
      const isSinglesMatchPlay = eventRes.data.game_type === "Singles Match Play";
      const calcHole = (pl, s, hole) => {
        let hcp;
        const plHcpAdj = (pl.hcp_adj ?? pl.handicap) || 0;
        if (isSinglesMatchPlay) {
          const groupmates = players.filter(p => p.grp === pl.grp);
          const lowest = Math.min(...groupmates.map(p => (p.hcp_adj ?? p.handicap) || 0));
          hcp = Math.round(plHcpAdj - lowest);
        } else {
          const hcpPct = isBetterBall ? 0.85 : (HCP_95_FORMATS.includes(eventRes.data.game_type) ? 0.95 : 1);
          hcp = Math.round(plHcpAdj * hcpPct);
        }
        const base = Math.floor(hcp / 18);
        const extra = hcp % 18;
        const shots = base + (hole.stroke_index <= extra ? 1 : 0);
        const g = parseInt(s.gross);
        const n = g - shots;
        return { g, n, pts: Math.max(0, 2 + (hole.par - n)) };
      };

      // A Better Ball pair share one card — routes/scores.js always saves a
      // row for BOTH partners on every hole, with 0 for whoever wasn't the
      // counting score that hole (the blank left on the real paper card).
      // partnerOf lets each player look up their partner so the pair's
      // actual better score can be used, instead of treating that 0 as a
      // real (and impossibly good) score of its own.
      const partnerOf = {};
      if (isBetterBall) {
        const pairMap = {};
        players.forEach(p => {
          const key = p.grp + ':' + (p.team_id || 0);
          if (!pairMap[key]) pairMap[key] = [];
          pairMap[key].push(p);
        });
        Object.values(pairMap).forEach(members => {
          if (members.length === 2) {
            partnerOf[members[0].player_id] = members[1];
            partnerOf[members[1].player_id] = members[0];
          }
        });
      }

      const playerResults = players.map(player => {
        const playerScores = scores.filter(s => s.player_id === player.player_id);
        const partner = partnerOf[player.player_id];
        const partnerScores = partner ? scores.filter(s => s.player_id === partner.player_id) : [];
        let gross = 0, points = 0, net = 0, thru = 0;
        const holePoints = [];
        const holeGross = [];
        const holeNet = [];
        holes.forEach(hole => {
          const s = playerScores.find(s => s.hole === hole.hole);
          const ownValid = s && parseInt(s.gross) > 0;
          const pRow = partner ? partnerScores.find(ps => ps.hole === hole.hole) : null;
          const partnerValid = pRow && parseInt(pRow.gross) > 0;

          if (ownValid || partnerValid) {
            const ownCalc = ownValid ? calcHole(player, s, hole) : null;
            const partnerCalc = partnerValid ? calcHole(partner, pRow, hole) : null;
            let chosen;
            if (ownCalc && partnerCalc) {
              chosen = isMedal ? (ownCalc.n <= partnerCalc.n ? ownCalc : partnerCalc) : (ownCalc.pts >= partnerCalc.pts ? ownCalc : partnerCalc);
            } else {
              chosen = ownCalc || partnerCalc;
            }
            net += chosen.n; points += chosen.pts; thru++;
            gross += ownValid ? ownCalc.g : 0;
            holePoints.push(chosen.pts);
            holeGross.push(ownValid ? ownCalc.g : 0);
            holeNet.push(chosen.n);
          } else { holePoints.push(0); holeGross.push(0); holeNet.push(0); }
        });
        // Countback must be computed from NET for Medal/Stroke Play, not
        // points -- confirmed as a genuine bug 2026-10-25 via the AI
        // Scoring Verification Wizard: back9/last6/last3/last1 were always
        // derived from holePoints (Stableford points) regardless of
        // format, even though the isMedal sort below compares them as if
        // they were net-based countback. A tied player's final position
        // could come out wrong as a result. Points-based countback is
        // still correct and unchanged for Stableford/Match Play formats.
        const countbackSource = isMedal ? holeNet : holePoints;
        const back9 = countbackSource.slice(9).reduce((a,b) => a+b, 0);
        const last6 = countbackSource.slice(12).reduce((a,b) => a+b, 0);
        const last3 = countbackSource.slice(15).reduce((a,b) => a+b, 0);
        const last1 = countbackSource[17] || 0;
        return { ...player, gross: gross||"-", net: net||"-", points: points||"-", thru, back9, last6, last3, last1, holePoints, holeGross, holeNet, hcp_review: !!player.hcp_review, verified_by_name: player.verified_by_name || null };
      });
      playerResults.sort((a, b) => {
        if (isMedal) {
          if (a.net === "-") return 1; if (b.net === "-") return -1;
          if (a.net !== b.net) return a.net - b.net;
          if (a.back9 !== b.back9) return a.back9 - b.back9;
          if (a.last6 !== b.last6) return a.last6 - b.last6;
          if (a.last3 !== b.last3) return a.last3 - b.last3;
          return a.last1 - b.last1;
        } else {
          if (a.points === "-") return 1; if (b.points === "-") return -1;
          if (a.points !== b.points) return b.points - a.points;
          if (a.back9 !== b.back9) return b.back9 - a.back9;
          if (a.last6 !== b.last6) return b.last6 - a.last6;
          if (a.last3 !== b.last3) return b.last3 - a.last3;
          return b.last1 - a.last1;
        }
      });
      let pos = 1;
      playerResults.forEach((p, i) => {
        if (i === 0) { p.pos = 1; }
        else {
          const prev = playerResults[i-1];
          // Fixed 2026-10-25 (found while verifying the countback fix
          // above, via the AI Scoring Verification Wizard): the Medal
          // branch previously only compared net totals here, never
          // back9/last6/last3/last1, so two players tied on net but
          // genuinely separated by countback were shown sharing a
          // position even though the sort above (correctly) ranked them
          // apart. Now checks the full countback, same as Stableford.
          const tied = isMedal
            ? (p.net === prev.net && p.back9 === prev.back9 && p.last6 === prev.last6 && p.last3 === prev.last3 && p.last1 === prev.last1)
            : (p.points === prev.points && p.back9 === prev.back9 && p.last6 === prev.last6 && p.last3 === prev.last3 && p.last1 === prev.last1);
          p.pos = tied ? prev.pos : pos;
        }
        pos = i + 2;
      });
      setResults(playerResults);
      // Additive-only hook for the AI Scoring Verification Wizard's
      // headless-Chrome reader (Component A) — exposes the already-computed
      // result array exactly as calculated above. Never touches the
      // calculation itself; harmless in every normal browser session.
      window.__wizardResult = playerResults;
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openMultiDay = async () => {
    try {
      const res = await api.get("/archive/list");
      setArchivedEvents(res.data || []);
      setShowMultiDay(true);
    } catch(e) { showToast("Failed to load archived events"); }
  };

  const saveMultiDay = async () => {
    if (!multiDayName.trim()) { showToast("Please enter a competition name"); return; }
    if (selectedEventIds.length < 2) { showToast("Please select at least 2 days"); return; }
    setMultiDayBusy(true);
    try {
      await api.post("/archive/multiday", { name: multiDayName, description: multiDayDesc, archived_event_ids: selectedEventIds });
      showToast("Multi-day competition saved!");
      setShowMultiDay(false);
      setMultiDayName(""); setMultiDayDesc(""); setSelectedEventIds([]);
    } catch(e) { showToast("Failed to save: " + (e.response?.data?.error || e.message)); }
    finally { setMultiDayBusy(false); }
  };

  const isMedal = event?.game_type?.includes("Medal") || event?.game_type?.includes("Stroke");
  const isHcpReviewFormat = HCP_REVIEW_FORMATS.includes(event?.game_type);
  const submitted = results.filter(p => p.hcp_review);
  const notSubmitted = results.filter(p => !p.hcp_review);

  const persistPrizes = async (confirm) => {
    try {
      await api.put("/events/prizes", {
        nearest_pin: nearestPinRef.current,
        nearest_pin2: nearestPin2Ref.current,
        longest_drive: longestDriveRef.current
      });
      if (confirm) showToast("Prizes saved!");
    } catch(e) { showToast("Failed to save prize: " + (e.response?.data?.error || e.message)); }
  };

  const archiveResults = async () => {
    if (!event) return;
    try {
      // Archiving used to only keep the final totals — the hole-by-hole
      // detail was lost the moment the next round's Reset Game cleared the
      // live hole_scores table, with no way to get it back afterwards.
      // Sending each hole's gross alongside the totals means that detail
      // survives permanently once archived.
      const archiveData = results.map(p => ({
        position: p.pos, rank: p.pos, player_name: p.name, handicap: p.handicap,
        gross: p.gross === "-" ? 0 : p.gross, net: p.net === "-" ? 0 : p.net, points: p.points === "-" ? 0 : p.points,
        holes: holes.map((h, i) => ({ hole: h.hole, gross: (p.holeGross || [])[i] || 0 })).filter(hh => hh.gross > 0)
      }));
      const twos = [];
      results.forEach(p => { (p.holeGross || []).forEach((g, i) => { if (g === 2) twos.push({ player_name: p.name, hole: i + 1 }); }); });
      const res = await api.post("/archive/save", { event_id: event.id, results: archiveData, nearest_pin: nearestPinRef.current, nearest_pin2: nearestPin2Ref.current, longest_drive: longestDriveRef.current, twos });
      await api.put("/events/prizes", { nearest_pin: nearestPinRef.current, nearest_pin2: nearestPin2Ref.current, longest_drive: longestDriveRef.current });
      showToast("Results archived!");
      // Pin High's own post-competition handicap adjustment (backend:
      // pinhighHandicapRules.js) — confirm exactly what changed so the
      // organiser can see it happened, rather than handicaps silently
      // updating in the background with nothing to show for it.
      const changes = res.data?.handicapChanges;
      if (changes && changes.length) {
        const lines = changes.map(c => `${c.name}: ${c.from} → ${c.to}`).join("\n");
        window.alert("Handicaps adjusted:\n\n" + lines);
      }
    } catch(e) { showToast("Failed: " + e.message); }
  };

  const exportResults = () => {
    if (results.length === 0) { showToast("No results to export"); return; }
    const headers = ["Pos", "Player", "WHS HCP", "Course HCP", "Thru", "Gross", "Net"];
    if (!isMedal) headers.push("Points");
    headers.push("Back 9", "Last 6", "Last 3", "18th");
    const aoa = [headers];
    results.forEach(p => {
      const row = [p.pos, p.name, fmtHcp(p.handicap), p.hcp_adj ?? p.handicap, p.thru, p.gross, p.net];
      if (!isMedal) row.push(p.points);
      row.push(p.back9, p.last6, p.last3, p.last1);
      aoa.push(row);
    });
    aoa.push([]);
    aoa.push(["Date:", event?.date ? new Date(event.date).toLocaleDateString('en-GB') : ""]);
    aoa.push(["Course:", event?.venue || ""]);
    aoa.push(["Format:", event?.game_type || ""]);
    const prizeRows = [];
    if (nearestPinRef.current) prizeRows.push(["Nearest Pin:", nearestPinRef.current]);
    if (nearestPin2Ref.current) prizeRows.push(["Nearest Pin 2:", nearestPin2Ref.current]);
    if (longestDriveRef.current) prizeRows.push(["Longest Drive:", longestDriveRef.current]);
    results.forEach(p => { (p.holeGross || []).forEach((g, i) => { if (g === 2) prizeRows.push(["Two (Hole " + (i + 1) + "):", p.name]); }); });
    if (prizeRows.length > 0) {
      aoa.push([]);
      aoa.push(["Prizes"]);
      aoa.push(...prizeRows);
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), "Results");
    XLSX.writeFile(wb, `Results_${(event?.date || 'event').toString().split('T')[0]}.xlsx`);
  };

  const exportHcpReview = () => {
    if (submitted.length === 0) { showToast("No players have been marked for HCP review"); return; }
    const sheetData = submitted.map(p => ({ Player: p.name, "WHS HCP": fmtHcp(p.handicap), "Course HCP": p.hcp_adj ?? p.handicap, Gross: p.gross, Net: p.net, Points: isMedal ? "" : p.points, Verified: p.verified_by_name ? ("Yes — by " + p.verified_by_name) : "No", Date: event?.date ? new Date(event.date).toLocaleDateString('en-GB') : "", Course: event?.venue || "", Format: event?.game_type || "" }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetData), "HCP Review");
    XLSX.writeFile(wb, `HCP_Review_${(event?.date || 'event').toString().split('T')[0]}.xlsx`);
  };

  const doUnlock = async () => {
    if (!unlockReason.trim()) { showToast("Please enter a reason for unlocking"); return; }
    setUnlockBusy(true);
    try {
      await api.post("/player/unlock", { event_id: event.id, player_id: unlockModal.player_id, reason: unlockReason });
      showToast("Score unlocked — " + unlockModal.name + " can now be edited");
      setUnlockModal(null);
      setUnlockReason("");
      await loadAll();
    } catch(e) { showToast("Failed: " + (e.response?.data?.error || e.message)); }
    finally { setUnlockBusy(false); }
  };

  const ResultsTable = ({ rows, headerColor, showVerified }) => (
    <table className="w-full text-sm border-collapse mb-6">
      <thead className="sticky top-0" style={{ backgroundColor: headerColor }}>
        <tr className="text-white">
          <th className="px-4 py-2 text-center w-12">Pos</th>
          <th className="px-4 py-2 text-left">Player</th>
          <th className="px-4 py-2 text-center w-16" title="Course Handicap (WHS HCP adjusted for slope/rating, capped if set)">Course HCP</th>
          <th className="px-4 py-2 text-center w-16">Thru</th>
          <th className="px-4 py-2 text-center w-16">Gross</th>
          <th className="px-4 py-2 text-center w-16">Net</th>
          {!isMedal && <th className="px-4 py-2 text-center w-16">Pts</th>}
          <th className="px-4 py-2 text-center w-16">B9</th>
          <th className="px-4 py-2 text-center w-16">L6</th>
          <th className="px-4 py-2 text-center w-16">L3</th>
          <th className="px-4 py-2 text-center w-16">L1</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p, i) => (
          <tr key={p.player_id} onClick={() => setSelectedPlayer(sp => (sp && sp.player_id === p.player_id) ? null : p)} style={{cursor: p.holePoints ? "pointer" : "default"}} className={"border-b border-gray-200 " + (i%2===0 ? "bg-white" : "bg-green-50") + (selectedPlayer && selectedPlayer.player_id === p.player_id ? " ring-2 ring-inset ring-green-600" : "")}>
            <td className="px-4 py-2 text-center font-bold">{p.pos}</td>
            <td className={"px-4 py-2 font-medium" + ((p.holeGross || []).some(g => g === 2) ? " text-blue-700 font-bold" : "")}>
              {p.name}
              {(p.holeGross || []).some(g => g === 2) && (
                <span className="ml-2 text-xs font-bold text-blue-700">Twos Scored</span>
              )}
              {showVerified && p.verified_by_name && (
                <span className="ml-2 inline-flex items-center gap-1">
                  <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                    ✅ Verified by {p.verified_by_name}
                  </span>
                  <button
                    onClick={() => { setUnlockModal(p); setUnlockReason(""); }}
                    className="text-xs text-red-500 hover:text-red-700 font-semibold px-1.5 py-0.5 rounded border border-red-200 hover:border-red-400"
                    title="Unlock this verified score">
                    🔓 Unlock
                  </button>
                </span>
              )}
            </td>
            <td className="px-4 py-2 text-center">{p.hcp_adj ?? p.handicap}</td>
            <td className="px-4 py-2 text-center">{p.thru}</td>
            <td className="px-4 py-2 text-center">{p.gross}</td>
            <td className="px-4 py-2 text-center">{p.net}</td>
            {!isMedal && <td className="px-4 py-2 text-center font-bold text-blue-700">{p.points}</td>}
            <td className="px-4 py-2 text-center text-gray-500">{p.back9}</td>
            <td className="px-4 py-2 text-center text-gray-500">{p.last6}</td>
            <td className="px-4 py-2 text-center text-gray-500">{p.last3}</td>
            <td className="px-4 py-2 text-center text-gray-500">{p.last1}</td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={isMedal ? 10 : 11} className="text-center py-4 text-gray-400 text-xs">None</td></tr>
        )}
      </tbody>
    </table>
  );

  return (
    <Layout>
      {toast && <div style={{position:"fixed",bottom:"20px",right:"20px",zIndex:9999}} className="bg-green-600 text-white px-4 py-3 rounded shadow-lg text-sm font-medium">{toast}</div>}

      {unlockModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"440px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#b91c1c",marginBottom:"8px"}}>🔓 Unlock Verified Score</div>
            <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>
              You are about to unlock <strong>{unlockModal.name}</strong>'s verified score. This will clear the verification and allow the score to be edited again. This action is logged.
            </div>
            <label style={{fontSize:"12px",fontWeight:"600",color:"#374151",display:"block",marginBottom:"6px"}}>Reason for unlocking (required)</label>
            <textarea
              value={unlockReason}
              onChange={e => setUnlockReason(e.target.value)}
              placeholder="e.g. Score entry error on hole 7 — player reported incorrect gross score"
              rows={3}
              style={{width:"100%",border:"2px solid #fca5a5",borderRadius:"8px",padding:"8px 12px",fontSize:"13px",resize:"vertical",boxSizing:"border-box",marginBottom:"16px"}}
              autoFocus
            />
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => { setUnlockModal(null); setUnlockReason(""); }}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={doUnlock} disabled={unlockBusy || !unlockReason.trim()}
                style={{background:"#b91c1c",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:unlockBusy||!unlockReason.trim()?0.5:1}}>
                {unlockBusy ? "Unlocking..." : "Confirm Unlock"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm">Results</span>
          {event && <span className="text-xs text-blue-600 font-medium">{event.game_type}</span>}
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <div className="flex flex-col items-center">
              <label className="text-xs font-bold text-gray-600 mb-0.5">Nearest Pin</label>
              <input key={"np-" + (prizes?.nearest_pin || "")} type="text" defaultValue={prizes?.nearest_pin || ""} onChange={e => nearestPinRef.current = e.target.value} onBlur={persistPrizes} placeholder="Player name" className="border-2 border-green-500 rounded px-2 py-1 text-sm font-semibold w-32" />
            </div>
            <div className="flex flex-col items-center">
              <label className="text-xs font-bold text-gray-600 mb-0.5">Nearest Pin 2</label>
              <input key={"np2-" + (prizes?.nearest_pin2 || "")} type="text" defaultValue={prizes?.nearest_pin2 || ""} onChange={e => nearestPin2Ref.current = e.target.value} onBlur={persistPrizes} placeholder="Player name" className="border-2 border-green-500 rounded px-2 py-1 text-sm font-semibold w-32" />
            </div>
            <div className="flex flex-col items-center">
              <label className="text-xs font-bold text-gray-600 mb-0.5">Longest Drive</label>
              <input key={"ld-" + (prizes?.longest_drive || "")} type="text" defaultValue={prizes?.longest_drive || ""} onChange={e => longestDriveRef.current = e.target.value} onBlur={persistPrizes} placeholder="Player name" className="border-2 border-green-500 rounded px-2 py-1 text-sm font-semibold w-32" />
            </div>
            <button type="button" onClick={() => persistPrizes(true)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded self-end">💾 Save Prizes</button>
            <button type="button" onClick={exportResults} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded self-end">📊 Export to Excel</button>
            {isHcpReviewFormat && (
              <button type="button" onClick={exportHcpReview} className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded self-end">📊 Export HCP Review</button>
            )}
            <button type="button" onClick={archiveResults} className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold px-3 py-1.5 rounded self-end">Archive Results</button>
            <button type="button" onClick={openMultiDay} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded self-end">Archive Multi Day</button>
          </div>
        </div>
        <div className="flex-1 overflow-auto px-0 py-2">
          {isHcpReviewFormat ? (
            <>
              <div className="px-4 pt-2 pb-1 text-sm font-bold text-green-800 bg-green-50 border-y border-green-200">
                ✅ Submitted for HCP Review ({submitted.length})
              </div>
              <ResultsTable rows={submitted} headerColor="#166534" showVerified={true} />
              <div className="px-4 pt-2 pb-1 text-sm font-bold text-gray-700 bg-gray-100 border-y border-gray-300">
                Not Submitted for HCP Review ({notSubmitted.length})
              </div>
              <ResultsTable rows={notSubmitted} headerColor="#6b7280" showVerified={false} />
            </>
          ) : (
            <ResultsTable rows={results} headerColor="#166534" showVerified={false} />
          )}
          {!loading && results.length === 0 && <div className="text-center py-8 text-gray-500">No scores entered yet.</div>}

          {selectedPlayer && selectedPlayer.holePoints && (
            <div className="px-4 pb-4">
              <div className="text-sm font-bold text-green-800 mb-2">Hole-by-hole — {selectedPlayer.name} (Course HCP {selectedPlayer.hcp_adj ?? selectedPlayer.handicap})</div>
              <div style={{overflowX: "auto"}}>
                <table className="text-xs border-collapse">
                  <thead>
                    <tr style={{backgroundColor: "#166534"}} className="text-white">
                      <th className="px-2 py-1 text-left">Hole</th>
                      {selectedPlayer.holeGross.map((_, i) => <th key={i} className="px-2 py-1 text-center">{i + 1}</th>)}
                      <th className="px-2 py-1 text-center">OUT</th>
                      <th className="px-2 py-1 text-center">IN</th>
                      <th className="px-2 py-1 text-center">TOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white border-b border-gray-200">
                      <td className="px-2 py-1 font-semibold">Strokes</td>
                      {selectedPlayer.holeGross.map((g, i) => <td key={i} className="px-2 py-1 text-center">{g || "-"}</td>)}
                      <td className="px-2 py-1 text-center font-semibold">{selectedPlayer.holeGross.slice(0, 9).reduce((a, b) => a + b, 0)}</td>
                      <td className="px-2 py-1 text-center font-semibold">{selectedPlayer.holeGross.slice(9).reduce((a, b) => a + b, 0)}</td>
                      <td className="px-2 py-1 text-center font-bold">{selectedPlayer.gross}</td>
                    </tr>
                    <tr className="bg-green-50">
                      <td className="px-2 py-1 font-semibold">Points</td>
                      {selectedPlayer.holePoints.map((pt, i) => <td key={i} className={"px-2 py-1 text-center" + (pt === 0 ? " bg-pink-100" : "")}>{pt}</td>)}
                      <td className="px-2 py-1 text-center font-semibold">{selectedPlayer.holePoints.slice(0, 9).reduce((a, b) => a + b, 0)}</td>
                      <td className="px-2 py-1 text-center font-semibold">{selectedPlayer.holePoints.slice(9).reduce((a, b) => a + b, 0)}</td>
                      <td className="px-2 py-1 text-center font-bold text-white" style={{backgroundColor: "#166534"}}>{selectedPlayer.points}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {showMultiDay && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"520px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#3730a3",marginBottom:"16px"}}>📅 Archive Multi Day Competition</div>
            <div style={{marginBottom:"12px"}}>
              <label style={{fontSize:"12px",fontWeight:"600",color:"#374151",display:"block",marginBottom:"4px"}}>Competition Name</label>
              <input type="text" value={multiDayName} onChange={e => setMultiDayName(e.target.value)}
                placeholder="e.g. Celtic Manor 2026"
                style={{width:"100%",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 12px",fontSize:"14px",boxSizing:"border-box"}} />
            </div>
            <div style={{marginBottom:"16px"}}>
              <label style={{fontSize:"12px",fontWeight:"600",color:"#374151",display:"block",marginBottom:"4px"}}>Description (optional)</label>
              <input type="text" value={multiDayDesc} onChange={e => setMultiDayDesc(e.target.value)}
                placeholder="e.g. Roman Road Day 1 & Montgomerie Day 2"
                style={{width:"100%",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 12px",fontSize:"14px",boxSizing:"border-box"}} />
            </div>
            <div style={{marginBottom:"16px"}}>
              <label style={{fontSize:"12px",fontWeight:"600",color:"#374151",display:"block",marginBottom:"8px"}}>Select Days (in order)</label>
              <div style={{maxHeight:"200px",overflowY:"auto",border:"1px solid #e5e7eb",borderRadius:"8px"}}>
                {archivedEvents.map((ae, i) => (
                  <label key={ae.id} style={{display:"flex",alignItems:"center",gap:"10px",padding:"8px 12px",borderBottom:"1px solid #f3f4f6",cursor:"pointer",background:selectedEventIds.includes(ae.id)?"#eef2ff":"white"}}>
                    <input type="checkbox" checked={selectedEventIds.includes(ae.id)}
                      onChange={e => setSelectedEventIds(prev => e.target.checked ? [...prev, ae.id] : prev.filter(id => id !== ae.id))}
                      style={{width:"16px",height:"16px",accentColor:"#4f46e5"}} />
                    <span style={{fontSize:"13px"}}>
                      <strong>{ae.date ? new Date(ae.date).toLocaleDateString("en-GB") : "No date"}</strong>
                      {" — "}{ae.venue || ae.course_name || ae.game_type}
                      {selectedEventIds.includes(ae.id) && <span style={{color:"#4f46e5",marginLeft:"8px",fontWeight:"700"}}>Day {selectedEventIds.indexOf(ae.id)+1}</span>}
                    </span>
                  </label>
                ))}
                {archivedEvents.length === 0 && <div style={{padding:"16px",color:"#9ca3af",fontSize:"13px",textAlign:"center"}}>No archived events found</div>}
              </div>
            </div>
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => { setShowMultiDay(false); setSelectedEventIds([]); }}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={saveMultiDay} disabled={multiDayBusy}
                style={{background:"#4f46e5",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:multiDayBusy?0.7:1}}>
                {multiDayBusy ? "Saving..." : "Save Multi Day Competition"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
